/**
 * Vector barcode re-render (ADR-011 mechanism 2): the decoded payload is
 * re-encoded as a crisp SVG on a WHITE background — max scanner contrast;
 * regenerated codes scan first time, photographed ones don't.
 *
 * Encoding uses zxing-wasm's writer submodule (writeBarcode → SVG string),
 * loaded via await import() ONLY — same lazy-chunk law as parse.ts
 * (ADR-000/ADR-011, enforced by the lint test). While the wasm chunk loads
 * (or when the format can't be re-encoded) the component renders nothing:
 * the caller ALWAYS shows the pinned raster underneath, so the barcode is
 * pure progressive enhancement — never a blank pass.
 */
import { useEffect, useState } from "react";
// `import type` is erased at compile time — stays outside the lazy-chunk law.
import type { WriteInputBarcodeFormat } from "zxing-wasm/writer";
import { PASS_BARCODE_FORMATS } from "./parse.ts";

const WRITABLE_FORMATS: ReadonlySet<string> = new Set(PASS_BARCODE_FORMATS);

/** Make the writer's fixed-size SVG scale to its container: guarantee a
 * viewBox, then drop the absolute width/height. Exported for tests. */
export function scalableSvg(svg: string): string | null {
  const open = svg.match(/<svg\b[^>]*>/);
  if (open === null) return null;
  let tag = open[0];
  if (!/viewBox=/.test(tag)) {
    const w = tag.match(/width="(\d+(?:\.\d+)?)"/);
    const h = tag.match(/height="(\d+(?:\.\d+)?)"/);
    if (w === null || h === null) return null; // no dimensions at all — can't scale safely
    tag = tag.replace("<svg", `<svg viewBox="0 0 ${w[1]} ${h[1]}"`);
  }
  tag = tag.replace(/\s(?:width|height)="[^"]*"/g, "");
  return svg.replace(open[0], tag);
}

export interface PassBarcodeProps {
  /** Decoded payload (PassRecord.barcode_text). */
  text: string;
  /** zxing format name (PassRecord.barcode_format). */
  format: string;
}

export function PassBarcode({ text, format }: PassBarcodeProps) {
  const [svg, setSvg] = useState<string | null>(null);

  useEffect(() => {
    setSvg(null);
    if (text === "" || !WRITABLE_FORMATS.has(format)) return;
    let cancelled = false;
    void (async () => {
      try {
        const { writeBarcode } = await import("zxing-wasm/writer");
        const result = await writeBarcode(text, {
          // Safe narrow: WRITABLE_FORMATS gates to encoder-supported names.
          format: format as WriteInputBarcodeFormat,
          withQuietZones: true, // scanners need the white margin as much as the modules
        });
        if (cancelled || result.error !== "" || result.svg === "") return;
        setSvg(scalableSvg(result.svg));
      } catch {
        /* wasm load/encode failure — raster fallback, never an error state */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [text, format]);

  if (svg === null) return null;
  return (
    <div
      // Inline on purpose: this block IS the contrast contract (white ground,
      // full width) — it must hold even if a stylesheet fails to load.
      style={{ background: "#fff", borderRadius: 8, padding: 12, width: "100%" }}
      // Safe by construction: the SVG string comes from zxing's encoder over
      // our own decoded payload, not from remote HTML.
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
